<template>
  <ZenniZnFilterFilterCategory
    v-if="facet"
    :title="title"
    :count="selectedProxy.length"
  >
    <ul class="grid grid-cols-5 gap-2 px-2">
      <li
        v-for="{ value, label } in computedFacetValues"
        :key="value"
        :aria-label="label"
        tabindex="0"
        @keydown.enter="() => processClick(value)"
        @keydown.space="() => processClick(value)"
      >
        <ZenniZnChip
          v-model="selectedProxy"
          class="peer-next-checked:!ring-1 w-full max-w-[80px]"
          :class="{
            'bg-grays-lighter !ring-grays-dark': selectedProxy.includes(value),
          }"
          size="sm"
          :input-props="{ value: value, 'aria-label': value }"
          :data-testid="`filter-chip-${value}`"
          @click="() => processClick(value)"
        >
          {{ label }}
        </ZenniZnChip>
      </li>
    </ul>
    <div class="pt-4 text-center">
      <ZenniZnFindYourSizeModal>
        <template #trigger>
          <div class="cursor-pointer space-x-2">
            <a class="!text-grays-darkest font-semibold underline">
              Find your size
            </a>
            <ZnIcon class="!w-6" type="size" size="xs" path="" altText="" />
          </div>
        </template>
      </ZenniZnFindYourSizeModal>
    </div>
  </ZenniZnFilterFilterCategory>
</template>

<script setup lang="ts">
import { computed, toRefs } from 'vue'
import { sumBy } from 'lodash-es'
import {
  MED_SIZE_GROUP,
  XS_SIZE_GROUP,
  XL_SIZE_GROUP,
} from '~/data/filter-groups'
import type { ChipSelectorProps, ChipSelectorEmits } from '~/types/filter'

const props = withDefaults(defineProps<ChipSelectorProps>(), {
  title: '',
  labelOrder: () => [],
})
const emit = defineEmits<ChipSelectorEmits>()

const selectedProxy = computed({
  get: () => props.selected,
  set: (value: ChipSelectorProps['selected']) => emit('update:selected', value),
})
const { title, facet } = toRefs(props)

const processClick = (value: string) =>
  selectedProxy.value.includes(value)
    ? (selectedProxy.value = selectedProxy.value.filter((v) => v !== value))
    : (selectedProxy.value = [...selectedProxy.value, value])
const transformGroupSizeToArray = (group: string) =>
  group.replace(/\[|\]/g, '').split(',')

const computedFacetValues = computed(() => {
  const MedSizeGroup = transformGroupSizeToArray(MED_SIZE_GROUP.filter)
  const XsSizeGroup = transformGroupSizeToArray(XS_SIZE_GROUP.filter)
  const XlSizeGroup = transformGroupSizeToArray(XL_SIZE_GROUP.filter)

  const withXSSize = facet.value?.values?.filter((facet) =>
    XsSizeGroup.includes(facet.value),
  )
  const xsSizeIndex = facet.value?.values?.findIndex((facet) =>
    XsSizeGroup.includes(facet.value),
  )

  const withMedSize = facet.value?.values?.filter((facet) =>
    MedSizeGroup.includes(facet.value),
  )
  const medSizeIndex = facet.value?.values?.findIndex((facet) =>
    MedSizeGroup.includes(facet.value),
  )

  const withXlSize = facet.value?.values?.filter((facet) =>
    XlSizeGroup.includes(facet.value),
  )
  const xlSizeIndex = facet.value?.values?.findIndex((facet) =>
    XlSizeGroup.includes(facet.value),
  )

  const xsSize = {
    label: 'XS',
    value: XS_SIZE_GROUP.filter,
    productCount: sumBy(withXSSize, 'productCount'),
  }

  const medSize = {
    label: 'M',
    value: MED_SIZE_GROUP.filter,
    productCount: sumBy(withMedSize, 'productCount'),
  }

  const xlSize = {
    label: 'XL',
    value: XL_SIZE_GROUP.filter,
    productCount: sumBy(withXlSize, 'productCount'),
  }

  const newFacetValues = facet.value?.values?.filter(
    (facet) =>
      !MedSizeGroup.includes(facet.value) &&
      !XsSizeGroup.includes(facet.value) &&
      !XlSizeGroup.includes(facet.value),
  )

  if (medSizeIndex !== undefined) {
    newFacetValues?.splice(medSizeIndex, 0, medSize)
  }
  if (xsSizeIndex !== undefined) {
    newFacetValues?.splice(xsSizeIndex, 0, xsSize)
  }
  if (xlSizeIndex !== undefined) {
    newFacetValues?.splice(xlSizeIndex, 0, xlSize)
  }

  return props.labelOrder.length
    ? newFacetValues?.sort(
        (a, b) =>
          props.labelOrder.indexOf(a.value) - props.labelOrder.indexOf(b.value),
      )
    : newFacetValues
})
</script>
